import { CanceledError } from "axios";

export type ApiError = Error & {
  apiRequest?: ApiErrorRequestContext;
  apiStatus?: number;
  isApiError?: true;
};

export type ApiErrorRequestContext = {
  method: string;
  online?: boolean;
  path: string;
  requestId?: string;
  transport?: "offline" | "timeout" | "network" | "unknown";
};

const ignoredBrowserMessages = [
  "Invalid call to runtime.sendMessage(). Tab not found.",
  "Importing a module script failed.",
];
const tanStackCancellationMessage = "CancelledError";

const redactPathSegment = (segment: string) => {
  const decodedSegment = decodeURIComponent(segment);

  if (/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(decodedSegment)) {
    return ":email";
  }

  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(decodedSegment)) {
    return ":uuid";
  }

  if (/^\d+$/.test(decodedSegment)) {
    return ":id";
  }

  if (decodedSegment.length > 64) {
    return ":value";
  }

  return segment;
};

export const sanitizeApiRequestPath = (url: string | undefined, baseURL?: string) => {
  if (!url) {
    return "unknown";
  }

  const origin = typeof window === "undefined" ? "https://veronics.local" : window.location.origin;
  const base = baseURL || origin;

  try {
    const pathname = new URL(url, base).pathname || "/";
    const sanitizedPath = pathname
      .split("/")
      .map((segment) => (segment ? redactPathSegment(segment) : segment))
      .join("/");

    return sanitizedPath;
  } catch {
    return "unknown";
  }
};

export const markApiError = (error: Error, status?: number, requestContext?: ApiErrorRequestContext): ApiError => {
  const apiError = error as ApiError;
  apiError.isApiError = true;
  apiError.apiStatus = status;
  apiError.apiRequest = requestContext;

  return apiError;
};

export const getApiErrorRequestContext = (error: unknown) => {
  if (!(error instanceof Error)) {
    return undefined;
  }

  return (error as ApiError).apiRequest;
};

const getStringProperty = (error: unknown, propertyName: string) => {
  if (!error || typeof error !== "object" || !(propertyName in error)) {
    return "";
  }

  const value = (error as Record<string, unknown>)[propertyName];
  return typeof value === "string" ? value : "";
};

const getErrorName = (error: unknown) => (error instanceof Error ? error.name : getStringProperty(error, "name"));

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : getStringProperty(error, "message") || String(error ?? "");

export const isCancellationError = (error: unknown) => {
  if (error instanceof CanceledError) {
    return true;
  }

  const name = getErrorName(error);
  const message = getErrorMessage(error);
  const code = getStringProperty(error, "code");

  return (
    name === "CanceledError" ||
    name === "CancelledError" ||
    name === "AbortError" ||
    code === "ERR_CANCELED" ||
    message === tanStackCancellationMessage ||
    message.includes("Query was cancelled by React Query") ||
    message.includes("canceled") ||
    message.includes("cancelled")
  );
};

export const isIgnoredThirdPartyError = (error: unknown) => {
  const message = getErrorMessage(error);

  return ignoredBrowserMessages.some((ignoredMessage) => message.includes(ignoredMessage));
};

const getApiStatus = (error: unknown) => {
  if (!(error instanceof Error)) {
    return undefined;
  }

  const apiError = error as ApiError;
  if (typeof apiError.apiStatus === "number") {
    return apiError.apiStatus;
  }

  const match = error.name.match(/^API Error (?<status>\d+)$/);
  const status = match?.groups?.status ? Number(match.groups.status) : undefined;

  return Number.isFinite(status) ? status : undefined;
};

export const isExpectedApiError = (error: unknown) => {
  const status = getApiStatus(error);

  return status === 400 || status === 401 || status === 403;
};

export const isOfflineTransportError = (error: unknown) => {
  return getApiErrorRequestContext(error)?.transport === "offline";
};

export const shouldReportFrontendError = (error: unknown) => {
  if (
    isCancellationError(error) ||
    isIgnoredThirdPartyError(error) ||
    isExpectedApiError(error) ||
    isOfflineTransportError(error)
  ) {
    return false;
  }

  return true;
};

type FrontendSentryStackFrame = {
  abs_path?: string;
  colno?: number;
  filename?: string;
  function?: string;
  in_app?: boolean;
  instruction_offset?: string;
  lineno?: number;
  module?: string;
  package?: string;
  platform?: string;
};

type FrontendSentryEvent = {
  contexts?: Record<string, unknown>;
  environment?: string;
  exception?: {
    values?: Array<{
      mechanism?: { handled?: boolean; type?: string };
      stacktrace?: { frames?: FrontendSentryStackFrame[] };
      type?: string;
      value?: string;
    }>;
  };
  fingerprint?: string[];
  level?: string;
  message?: string;
  platform?: string;
  release?: string;
  tags?: Record<string, unknown>;
  type?: string;
};

type FrontendSentryHint = {
  originalException?: unknown;
};

const approvedTransportValues = new Set<ApiErrorRequestContext["transport"]>([
  "offline",
  "timeout",
  "network",
  "unknown",
]);
const approvedHttpMethods = new Set(["DELETE", "GET", "HEAD", "OPTIONS", "PATCH", "POST", "PUT"]);
const emailPattern = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const phoneNumberPattern = /(?:\+?\d[\d().\-\s]{7,}\d)/g;
const bearerTokenPattern = /\b(Bearer\s+)[^\s,;]+/gi;
const queryStringPattern = /([?&][^\s&#=]+)=([^\s&#]*)/g;
const maxSentryMessageLength = 1024;

const isSafeApiStatus = (status: number | undefined) =>
  status !== undefined && Number.isInteger(status) && status >= 100 && status <= 599;

const isSafeRequestId = (requestId: string | undefined) =>
  requestId && requestId.length <= 128 ? requestId : undefined;

const sanitizeSentryMessage = (message: string | undefined) => {
  if (!message) {
    return undefined;
  }

  const sanitized = message
    .replace(bearerTokenPattern, "$1[redacted]")
    .replace(queryStringPattern, "$1=[redacted]")
    .replace(emailPattern, "[email]")
    .replace(phoneNumberPattern, "[phone]");

  return sanitized.length <= maxSentryMessageLength ? sanitized : `${sanitized.slice(0, maxSentryMessageLength - 1)}…`;
};

const sanitizeStackFrame = (frame: FrontendSentryStackFrame) => ({
  ...(typeof frame.abs_path === "string" && { abs_path: frame.abs_path }),
  ...(typeof frame.colno === "number" && { colno: frame.colno }),
  ...(typeof frame.filename === "string" && { filename: frame.filename }),
  ...(typeof frame.function === "string" && { function: frame.function }),
  ...(typeof frame.in_app === "boolean" && { in_app: frame.in_app }),
  ...(typeof frame.instruction_offset === "string" && { instruction_offset: frame.instruction_offset }),
  ...(typeof frame.lineno === "number" && { lineno: frame.lineno }),
  ...(typeof frame.module === "string" && { module: frame.module }),
  ...(typeof frame.package === "string" && { package: frame.package }),
  ...(typeof frame.platform === "string" && { platform: frame.platform }),
});

const sanitizeException = (exception: FrontendSentryEvent["exception"]) => {
  const values = exception?.values?.map((value) => {
    const message = sanitizeSentryMessage(value.value);

    return {
      ...(typeof value.type === "string" && { type: value.type }),
      ...(message && { value: message }),
      ...(value.mechanism && {
        mechanism: {
          ...(typeof value.mechanism.type === "string" && { type: value.mechanism.type }),
          ...(typeof value.mechanism.handled === "boolean" && { handled: value.mechanism.handled }),
        },
      }),
      ...(value.stacktrace?.frames && { stacktrace: { frames: value.stacktrace.frames.map(sanitizeStackFrame) } }),
    };
  });

  return values?.length ? { values } : undefined;
};

const sanitizeApiDiagnostics = (apiRequest: ApiErrorRequestContext, status: number | undefined) => {
  const method = apiRequest.method.toUpperCase();
  const path = sanitizeApiRequestPath(apiRequest.path);
  const transport = approvedTransportValues.has(apiRequest.transport) ? apiRequest.transport : undefined;
  const requestId = isSafeRequestId(apiRequest.requestId);

  return {
    contexts: {
      apiRequest: {
        method: approvedHttpMethods.has(method) ? method : "UNKNOWN",
        path,
        ...(isSafeApiStatus(status) && { status }),
        ...(transport && { transport }),
        ...(requestId && { requestId }),
      },
    },
    tags: {
      api_method: approvedHttpMethods.has(method) ? method : "UNKNOWN",
      api_path: path,
      ...(isSafeApiStatus(status) && { api_status: String(status) }),
      ...(requestId && { backend_request_id: requestId }),
      ...(transport && { api_transport: transport }),
    },
    ...(transport && { fingerprint: ["api-transport-error", transport, method, path] }),
  };
};

export const filterFrontendSentryEvent = <TEvent extends FrontendSentryEvent>(
  event: TEvent,
  hint: FrontendSentryHint,
) => {
  if (hint.originalException && !shouldReportFrontendError(hint.originalException)) {
    return null;
  }

  const message = event.exception?.values?.[0]?.value ?? event.message;
  if (message && !shouldReportFrontendError(new Error(message))) {
    return null;
  }

  const exception = sanitizeException(event.exception);
  const eventMessage = sanitizeSentryMessage(event.message);
  const sanitizedEvent: FrontendSentryEvent = {
    ...(typeof event.type === "string" && { type: event.type }),
    ...(typeof event.level === "string" && { level: event.level }),
    ...(typeof event.platform === "string" && { platform: event.platform }),
    ...(typeof event.release === "string" && { release: event.release }),
    ...(typeof event.environment === "string" && { environment: event.environment }),
    ...(eventMessage && { message: eventMessage }),
    ...(exception && { exception }),
  };

  const apiRequest = getApiErrorRequestContext(hint.originalException);
  if (apiRequest) {
    Object.assign(sanitizedEvent, sanitizeApiDiagnostics(apiRequest, getApiStatus(hint.originalException)));
  }

  return sanitizedEvent as TEvent;
};
